import { isEnglishUi, pickLocalizedText } from './uiLocale'
import {
  isCornerXlOrejaCatalog,
  unitArmHandednessLetter,
} from '@utils/linkedArms/singleArmSeriesRules'

function normalizeLabelKey(value: string): string {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\s*\/\s*/g, '/')
    .replace(/\s+/g, ' ')
    .trim()
}

/**
 * Etiquetas de menú / descripción que vienen del back en español.
 * Menú principal Artisan: MÓDULOS, RINCÓN, CORNER XL… (CORNER/RELAX/POUFF/ARM se dejan).
 */
const CATALOG_LABEL_EN: Record<string, string> = {
  'brazo/respaldo': 'ARM/BACK',
  'brazo / respaldo': 'ARM/BACK',
  brazo: 'ARM',
  respaldo: 'BACK',
  base: 'BASE',
  cojines: 'CUSHIONS',
  cojin: 'CUSHION',
  'cojin / asiento': 'CUSHION / SEAT',
  // Menú unidades (no asiento/cojines)
  modulos: 'MODULES',
  modulo: 'MODULE',
  rincon: 'CORNER',
  'modulo sin brazos': 'Armless module',
  'modulos sin brazos': 'Armless modules',
  'corner xl oreja': 'CORNER XL WING',
  oreja: 'WING',
}

/**
 * Localiza etiquetas de catálogo/menú.
 * En español se deja el texto original; en inglés se traduce si hay mapeo.
 * Términos ya bilingües (CORNER, RELAX, POUFF, ARM) se mantienen.
 */
export function localizeCatalogLabel(raw?: string): string {
  const value = String(raw || '').trim()
  if (!value) return ''
  if (!isEnglishUi()) return value

  const key = normalizeLabelKey(value)
  if (CATALOG_LABEL_EN[key]) return CATALOG_LABEL_EN[key]

  // "Brazo / Respaldo" con espacios variables
  const compact = key.replace(/\s+/g, '')
  if (CATALOG_LABEL_EN[compact]) return CATALOG_LABEL_EN[compact]

  // Frases frecuentes dentro de descripciones más largas
  let text = value
  text = text.replace(/\bm[oó]dulo\s+sin\s+brazos\b/gi, 'Armless module')
  text = text.replace(/\bm[oó]dulos\s+sin\s+brazos\b/gi, 'Armless modules')
  text = text.replace(/\bCORNER\s+XL\s+OREJA\b/gi, 'CORNER XL WING')

  // Traducción parcial por términos sueltos
  text = text.replace(/\bM[ÓO]DULOS\b/gi, 'MODULES')
  text = text.replace(/\bM[ÓO]DULO\b/gi, 'MODULE')
  text = text.replace(/\bRINC[ÓO]N\b/gi, 'CORNER')
  text = text.replace(/\bOREJA\b/gi, 'WING')
  text = text.replace(/\bBRAZO\b/gi, 'ARM')
  text = text.replace(/\bRESPALDO\b/gi, 'BACK')
  text = text.replace(/\bCOJINES\b/gi, 'CUSHIONS')
  text = text.replace(/\bCOJÍN\b/gi, 'CUSHION')
  text = text.replace(/\bCOJIN\b/gi, 'CUSHION')
  text = text.replace(/\bALTURA\b/gi, 'HEIGHT')
  text = text.replace(/\bsin\s+brazos\b/gi, 'armless')

  // BASE solo si es la palabra entera del label (evita "BASED").
  if (normalizeLabelKey(text) === 'base') return 'BASE'

  return text
}

const CORNER_XL_OREJA_TITLES = {
  i: { es: 'Oreja Derecha de', en: 'Right Wing of' },
  d: { es: 'Oreja Izquierda de', en: 'Left Wing of' },
} as const

const CORNER_XL_OREJA_DETAIL = { es: '30', en: '30' }

export function resolveCornerXlOrejaDisplayParts(
  catalogID?: unknown,
): { title: string; detail: string } | null {
  if (!isCornerXlOrejaCatalog(catalogID)) return null
  const letter = unitArmHandednessLetter(catalogID)
  if (letter !== 'i' && letter !== 'd') return null
  return {
    title: pickLocalizedText(CORNER_XL_OREJA_TITLES[letter]),
    detail: pickLocalizedText(CORNER_XL_OREJA_DETAIL),
  }
}

/**
 * Título de menú / SELECCIÓN para Corner XL Oreja (177–180).
 * Catálogo …I → oreja derecha; …D → oreja izquierda. El presupuesto no usa esto.
 */
export function resolveCornerXlOrejaDisplayLabel(catalogID?: unknown): string | null {
  const parts = resolveCornerXlOrejaDisplayParts(catalogID)
  if (!parts) return null
  return `${parts.title} ${parts.detail}`
}
